import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button, Flex, SegmentedControl, Text, Tooltip } from '@mantine/core';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { SAMPLE_INVESTOR_ID } from '@dealstack/shared/lib/documents/fieldBindingCatalogue';
import { getFileReactQuery } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { DocumentViewerHeader } from '@dealstack/frontend/shared/components/DocumentViewerV2/common/DocumentViewerHeader.tsx';
import { BindPanel } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/BindPanel.tsx';
import { BindPanelEmptyState } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/BindPanelEmptyState.tsx';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import type { PanelHeaderEdit } from '@dealstack/frontend/shared/components/FieldBinding/TemplateNameField/templateNameField.types.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import {
	PaletteTab,
	PaletteTabs,
	PaletteTabsList,
} from '@dealstack/frontend/shared/components/DocumentEditor/flowDocumentEditor.styled';

import { PdfPageCanvas } from '../PdfCanvas/PdfPageCanvas.tsx';
import { FieldBindingOverlay } from '../FieldBinding/FieldBindingCanvas/FieldBindingOverlay.tsx';
import { useFieldBindingCanvas } from '../FieldBinding/FieldBindingCanvas/fieldBindingCanvas.hooks.ts';
import { PreviewAsSelect } from '../FieldBinding/PreviewAsSelect.tsx';
import { useInvestorOptions } from '../FieldBinding/useInvestorOptions.ts';
import { usePreviewInvestorShortcuts } from '../FieldBinding/usePreviewInvestorShortcuts.ts';
import {
	MAX_SCALE,
	MIN_SCALE,
	SCALE_STEP,
	DEFAULT_CANVAS_SCALE,
} from '../FieldBinding/FieldBindingEditor.types.ts';
import type { ViewMode } from '../FieldBinding/FieldBindingEditor.types.ts';
import type { FieldBindingEditorBinding } from '../FieldBinding/useFieldBinding.types.ts';
import {
	BindPanelSlot,
	CanvasPane,
	HeaderActionSlot,
	HeaderContent,
	Legend,
	LegendItem,
	LegendSwatch,
	Split,
	ToggleRow,
} from '../FieldBinding/FieldBindingEditor.styled.ts';
import { PlacementOverlay } from '../SignaturePlacement/PlacementCanvas/PlacementOverlay.tsx';
import { usePlacementCanvas } from '../SignaturePlacement/PlacementCanvas/PlacementCanvas.hooks.ts';
import { useSignaturePlacement } from '../SignaturePlacement/useSignaturePlacement.ts';
import { dealPlacementAdapter } from '../SignaturePlacement/dealPlacementAdapter.ts';
import type { PlacementAdapter } from '@dealstack/frontend/shared/components/FieldBinding/placementAdapter.ts';
import { buildPlacementSlotListItems } from '../SignaturePlacement/signaturePlacementShared.ts';
import { SlotList } from '../SignaturePlacement/SlotList/SlotList.tsx';
import type { DocumentEditorTab } from '../DocumentEditorModal/documentEditorModal.types.ts';

import {
	PanelFooter,
	PanelScroller,
	SidePanel,
	SidePanelHeader,
} from './pdfDocumentEditor.styled.ts';
import { formatSignatureSummary } from './PdfDocumentEditor.utils.ts';

interface Props {
	/** Null while the owner's template is unsaved; only an owner whose adapter handles that. */
	templateId: string | null;
	/**
	 * Who owns this template, for placement — see {@link PlacementAdapter}. Defaults to the
	 * deal's, which is the only caller that had one before KYC did.
	 */
	placementAdapter?: PlacementAdapter;
	file: Blob | string;
	binding: FieldBindingEditorBinding;
	requestPreview: (investorId: string | null) => Promise<{ fileId: string }>;
	/** Pinned above the tab bar, outside whichever panel is showing — the document's name.
	 *  Supplied as a node so this editor stays out of the form that owns the value. */
	panelHeader?: ReactNode;
	/** Folds an unsaved {@link panelHeader} edit into this editor's Save and Discard, so the
	 *  Save under it writes it rather than closing over the top of it. */
	panelHeaderEdit?: PanelHeaderEdit;
	/** Seeds the "Show details" switch from the template's saved showStampMetadata. */
	initialShowStampMetadata: boolean;
	/** False when the document has no signing roles: the canvas is then dates-only. */
	manualPlacementEnabled: boolean;
	postDatingEnabled: boolean;
	/** Why this document cannot use manual placement, when it cannot. */
	blockedReason?: string;
	initialPreviewInvestorId?: string | null;
	onPreviewInvestorChange?: (investorId: string | null) => void;
	/**
	 * Which jobs this document actually has.
	 *
	 * They differ: a template-backed document has no fields to bind — its values come from the
	 * pinned snapshot — but can still place signatures, and a document with no signing
	 * configuration and no post-dating has nothing to place. With only one job there is no tab
	 * bar, because a tab bar for one job is a label.
	 */
	showFieldsTab: boolean;
	showSignaturesTab: boolean;
	/** False hides "Preview as": nothing to render a preview against. Defaults to true. */
	previewEnabled?: boolean;
	onClose?: () => void;
}

/**
 * One PDF, one canvas, both jobs.
 *
 * Field binding and signature placement used to be separate full-screen editors over the same
 * file — two documents parsed, two zooms, two headers, and no way to see where the stamps would
 * land while binding a field. They are one surface now: the tab switches which side panel is
 * showing and which overlay may be dragged, and the OTHER overlay stays on the page, visible but
 * inert, so the author can see both halves of the setup at once.
 *
 * One Save writes both. The two are independent server calls, so they go together and the editor
 * only closes once each has landed — a failure in either leaves everything on screen untouched.
 */
export const PdfDocumentEditor = ({
	templateId,
	file,
	binding,
	requestPreview,
	panelHeader,
	panelHeaderEdit,
	initialShowStampMetadata,
	manualPlacementEnabled,
	postDatingEnabled,
	blockedReason,
	initialPreviewInvestorId,
	onPreviewInvestorChange,
	showFieldsTab,
	showSignaturesTab,
	previewEnabled = true,
	placementAdapter = dealPlacementAdapter,
	onClose,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	// Owned here, like the Word editor owns its own: the tab switches this editor's panel and
	// which overlay is live, and nothing outside needs to know which one is showing.
	const [tab, setTab] = useState<DocumentEditorTab>('fields');
	const [viewMode, setViewMode] = useState<ViewMode>('original');
	const [scale, setScale] = useState(DEFAULT_CANVAS_SCALE);
	const [showStampMetadata, setShowStampMetadata] = useState(initialShowStampMetadata);
	const [currentPageNum, setCurrentPageNum] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [selectedInvestorId, setSelectedInvestorId] = useState<string | null>(
		initialPreviewInvestorId ?? SAMPLE_INVESTOR_ID
	);

	const onUpscale = () => setScale(Math.min(MAX_SCALE, scale + SCALE_STEP));
	const onDownscale = () => setScale(Math.max(MIN_SCALE, scale - SCALE_STEP));

	const handlePreviewInvestorChange = (investorId: string | null) => {
		setSelectedInvestorId(investorId);
		onPreviewInvestorChange?.(investorId);
	};

	const {
		options: investorOptions,
		onDropdownOpen: onInvestorDropdownOpen,
		isFetching: isFetchingInvestors,
	} = useInvestorOptions({ enabled: previewEnabled, preload: viewMode === 'preview' });

	const {
		bindings,
		catalogue,
		securities,
		scopedSamples,
		uncollectedRequirements,
		isLoading,
		selectedFieldKey,
		setSelectedFieldKey,
		onChangeBinding,
		onApplySecurityToAll,
		onBoxChange,
		onAddBox,
		onRemove: onRemoveBinding,
		onRenameField,
		save: saveBindings,
		discard,
		isDirty,
		isSaving: isSavingBindings,
		boundCount,
		totalCount,
	} = binding;

	const placement = useSignaturePlacement(templateId, showStampMetadata, placementAdapter);
	const savePlacement = placementAdapter.useSavePlacement();

	const bindingCanvas = useFieldBindingCanvas({ bindings, scale, onAddBox });
	const placementCanvas = usePlacementCanvas({
		placements: placement.placementsByKey,
		selectedSlotKey: placement.selectedSlotKey,
		scale,
		slotByKey: placement.slotByKey,
		showMetadata: showStampMetadata,
		onVisiblePageChange: setCurrentPageNum,
		onPlacementChange: placement.onPlacementChange,
	});

	const previewMutation = useMutation({
		mutationFn: (investorId: string | null) => requestPreview(investorId),
	});

	// So what is on screen answers "what will this produce" for the bindings as saved.
	useEffect(() => {
		if (viewMode === 'preview' && selectedInvestorId) {
			previewMutation.mutate(selectedInvestorId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewMode, selectedInvestorId, templateId]);

	const previewFileId = previewMutation.data?.fileId ?? null;
	const { data: previewFileResponse, isFetching: isLoadingPreviewFile } = useQuery({
		queryKey: [API_PATHS.FILE_ID_GET, { id: previewFileId }, 'pdf-document-editor-preview'],
		queryFn: async ({ signal }) => {
			if (!previewFileId) {
				return undefined;
			}
			const response = await getFileReactQuery({ id: previewFileId }, signal);
			return response ? await response.blob() : undefined;
		},
		enabled: !!previewFileId,
	});

	const isPreviewMode = viewMode === 'preview';
	const isPreviewLoading = previewMutation.isPending || isLoadingPreviewFile;
	const previewUnavailable = selectedInvestorId == null;
	const showTabs = showFieldsTab && showSignaturesTab;
	// With one job the tab state is irrelevant — that job shows regardless.
	const onFields = showTabs ? tab === 'fields' : showFieldsTab;

	const slotListItems = useMemo(
		() =>
			buildPlacementSlotListItems({
				slots: manualPlacementEnabled ? placement.slots : [],
				placementsByKey: placement.placementsByKey,
				datePlacementKeys: placement.datePlacementKeys,
			}),
		[
			manualPlacementEnabled,
			placement.slots,
			placement.placementsByKey,
			placement.datePlacementKeys,
		]
	);

	const hasNoSignaturesPlaced = placement.signaturePlacements.length === 0;
	const hasEveryDateNeeded = !postDatingEnabled || placement.datePlacementKeys.length > 0;
	const isSaving = isSavingBindings || savePlacement.isSaving || !!panelHeaderEdit?.isSaving;

	usePreviewInvestorShortcuts({
		enabled: isPreviewMode && !isSaving,
		options: investorOptions,
		value: selectedInvestorId,
		onChange: handlePreviewInvestorChange,
	});

	const canSave =
		(!manualPlacementEnabled || placement.isComplete || hasNoSignaturesPlaced) &&
		hasEveryDateNeeded &&
		!placement.hasOverlappingPlacements &&
		!isSaving &&
		!blockedReason;

	/**
	 * One action, every write the panel is showing — bindings, placements, and whatever the
	 * panel header holds.
	 *
	 * Fired together rather than in sequence: none depends on another's result, and running them
	 * one after the next would leave a half-saved document on screen for the length of a round
	 * trip. `Promise.all` rejects on the first failure, so the editor stays open with everything
	 * intact and each mutation reports its own error as it always did.
	 */
	const handleSave = async () => {
		try {
			await Promise.all([
				// Only when the panel is showing them. A document with no Fields tab has nothing
				// to personalise, and writing its rows back would push bindings at a multi-sign
				// document that the server (rightly) refuses.
				showFieldsTab ? saveBindings() : undefined,
				panelHeaderEdit?.isDirty ? panelHeaderEdit.save() : undefined,
				savePlacement.save({
					templateId,
					useManualSignaturePlacement: manualPlacementEnabled && !hasNoSignaturesPlaced,
					showStampMetadata,
					signaturePlacements: manualPlacementEnabled
						? placement.signaturePlacements
						: undefined,
					requiresPostDating: postDatingEnabled,
					datePlacements: postDatingEnabled ? placement.datePlacements : [],
				}),
			]);
		} catch {
			// Swallowed on purpose: each mutation already reports its own failure, and what this
			// handler owes is simply NOT to close. Rethrowing would surface as an unhandled
			// rejection and close nothing anyway.
			return;
		}
		onClose?.();
	};

	const selectedField = bindings.find((row) => row.fieldKey === selectedFieldKey) ?? null;

	// The active tab's own progress: two counts side by side read as one number the author has
	// to reconcile, and only one of them is being worked on.
	const dateCount = placement.datePlacementKeys.length;
	const placedCount = placement.placementsByKey.size - dateCount;
	const headerTitle = onFields
		? tMipManage('master_documents.field_binding.progress', {
				bound: boundCount,
				total: totalCount,
			})
		: formatSignatureSummary({ placedCount, slotCount: placement.slots.length, dateCount });

	if (isLoading || placement.isLoading) {
		return <InlineLoader />;
	}

	return (
		<Flex w="100%" h="100%" direction="column" gap={8} mih={0}>
			<ToggleRow>
				<span />
				{previewEnabled && (
					<>
						<Tooltip
							label={tMipManage('master_documents.field_binding.preview_tooltip')}
							disabled={!previewUnavailable}
						>
							<span>
								<SegmentedControl
									data-testid="field-binding-view-toggle"
									value={viewMode}
									onChange={(value) => setViewMode(value as ViewMode)}
									disabled={previewUnavailable}
									data={[
										{
											label: tMipManage(
												'master_documents.field_binding.view.original'
											),
											value: 'original',
										},
										{
											label: tMipManage(
												'master_documents.field_binding.view.preview'
											),
											value: 'preview',
										},
									]}
								/>
							</span>
						</Tooltip>
						<PreviewAsSelect
							options={investorOptions}
							value={selectedInvestorId}
							onChange={handlePreviewInvestorChange}
							onDropdownOpen={onInvestorDropdownOpen}
							isFetching={isFetchingInvestors}
							disabled={isSaving}
							shortcutsActive={isPreviewMode}
						/>
					</>
				)}
				<HeaderActionSlot />
			</ToggleRow>

			<DocumentViewerHeader
				title={
					<HeaderContent>
						<Text fw={600}>{headerTitle}</Text>
						{onFields && (
							<Legend>
								<LegendItem>
									<LegendSwatch $variant="unbound" />
									<Text size="xs" c="dimmed">
										{tMipManage(
											'master_documents.field_binding.legend.needs_binding'
										)}
									</Text>
								</LegendItem>
								<LegendItem>
									<LegendSwatch $variant="bound" />
									<Text size="xs" c="dimmed">
										{tMipManage('master_documents.field_binding.legend.bound')}
									</Text>
								</LegendItem>
							</Legend>
						)}
					</HeaderContent>
				}
				currentPage={totalPages > 0 ? currentPageNum : undefined}
				totalPages={totalPages > 0 ? totalPages : undefined}
				onUpscale={onUpscale}
				onDownscale={onDownscale}
				onBackArrowClick={onClose}
				showBackButton
			/>

			<Split $singleColumn={isPreviewMode}>
				<CanvasPane>
					{isPreviewMode && (isPreviewLoading || !previewFileResponse) ? (
						<InlineLoader />
					) : (
						<PdfPageCanvas
							file={isPreviewMode && previewFileResponse ? previewFileResponse : file}
							scale={scale}
							pageDims={placementCanvas.pageDims}
							onPageRenderSuccess={(pageNum, page) => {
								placementCanvas.onPageRenderSuccess(pageNum, page);
								bindingCanvas.onPageRenderSuccess(pageNum, page);
							}}
							onTotalPagesChange={setTotalPages}
							// Only the template hides them; a preview is a finished render.
							hidePrefilledFormValues={!isPreviewMode}
							scrollHostRef={placementCanvas.scrollHostRef}
							pageRefs={placementCanvas.pageRefs}
							testId="pdf-document-canvas"
							pageTestId={(pageNum) => `pdf-document-canvas-page-${pageNum}`}
							errorText={tMipManage(
								'master_documents.field_binding.canvas.load_error'
							)}
							noDataText={tMipManage('master_documents.field_binding.canvas.no_file')}
							renderOverlay={({ pageNum, dims }) =>
								/* Nothing over a PREVIEW: those pages are a different render with
								   the values already in, and every box here is a coordinate on the
								   TEMPLATE. Drawn over the preview they would point at the wrong
								   place and invite an edit against a file this cannot save. */
								isPreviewMode ? null : (
									<>
										<FieldBindingOverlay
											pageNum={pageNum}
											dims={dims}
											scale={scale}
											bindings={
												bindingCanvas.bindingsByPage.get(pageNum) ?? []
											}
											selectedFieldKey={selectedFieldKey}
											draw={bindingCanvas.draw}
											interactive={onFields}
											onSelect={setSelectedFieldKey}
											onBoxChange={onBoxChange}
											onRemove={onRemoveBinding}
											onMouseDown={bindingCanvas.handleOverlayMouseDown}
											onMouseMove={bindingCanvas.handleOverlayMouseMove}
											onFinishDraw={bindingCanvas.finishDraw}
										/>
										<PlacementOverlay
											pageNum={pageNum}
											dims={dims}
											scale={scale}
											placements={
												placementCanvas.placementsByPage.get(pageNum) ?? []
											}
											fixedPlacements={[]}
											overlappingKeys={placementCanvas.overlappingKeys}
											slotByKey={placement.slotByKey}
											selectedSlotKey={placement.selectedSlotKey}
											showMetadata={showStampMetadata}
											interactive={!onFields}
											collectPages={placementCanvas.collectPages}
											obstaclesOnPage={placementCanvas.obstaclesOnPage}
											onSelect={placement.setSelectedSlotKey}
											onPlacementChange={placement.onPlacementChange}
											onRemove={placement.onRemove}
										/>
									</>
								)
							}
						/>
					)}
				</CanvasPane>

				{/* Hidden in preview mode: the preview is a real render of the SAVED bindings, so an
				    edit made against it reports success and changes nothing on the page in front of
				    the author. Read-only until they switch back. */}
				{!isPreviewMode && (
					<SidePanel data-testid="pdf-document-editor-side-panel">
						{panelHeader}
						{showTabs && (
							<PaletteTabs
								value={onFields ? 'fields' : 'signatures'}
								onChange={(value) =>
									setTab(value === 'signatures' ? 'signatures' : 'fields')
								}
								data-testid="document-editor-tabs"
							>
								<PaletteTabsList grow>
									<PaletteTab
										value="fields"
										data-testid="document-editor-tab-fields"
									>
										{tMipManage('master_documents.editor.tab_fields', {
											defaultValue: 'Data points',
										})}
									</PaletteTab>
									<PaletteTab
										value="signatures"
										data-testid="document-editor-tab-signatures"
									>
										{tMipManage('master_documents.editor.tab_signatures', {
											defaultValue: 'Signatures',
										})}
									</PaletteTab>
								</PaletteTabsList>
							</PaletteTabs>
						)}
						{onFields ? (
							<BindPanelSlot>
								{selectedField ? (
									<BindPanel
										key={selectedField.fieldKey}
										field={selectedField}
										catalogue={catalogue}
										securities={securities}
										scopedSamples={scopedSamples}
										uncollectedRequirements={uncollectedRequirements}
										onChangeBinding={onChangeBinding}
										onApplySecurityToAll={onApplySecurityToAll}
										onRenameField={onRenameField}
										onRemove={() => {
											onRemoveBinding(selectedField.fieldKey);
											setSelectedFieldKey(null);
										}}
										onClose={() => setSelectedFieldKey(null)}
									/>
								) : (
									<BindPanelEmptyState
										variant={totalCount === 0 ? 'no-fields' : 'none-selected'}
										unboundCount={totalCount - boundCount}
									/>
								)}
							</BindPanelSlot>
						) : (
							<>
								<SidePanelHeader>
									{blockedReason !== undefined && (
										<Text
											size="xs"
											c="red"
											data-testid="placement-blocked-reason"
										>
											{blockedReason}
										</Text>
									)}
									<AdvancedSwitch
										rightText="Show details"
										selectedValue={showStampMetadata}
										onChange={(event) => setShowStampMetadata(!!event)}
										selectedColor="dark.8"
										unselectedColor="dark.8"
									/>
									{postDatingEnabled && (
										<Button
											variant="light"
											onClick={() => placement.onAddDateField(currentPageNum)}
											data-testid={
												TEST_ELEMENT_IDS.PLACEMENT_EDITOR_ADD_DATE_FIELD_BUTTON
											}
										>
											Add date field
										</Button>
									)}
								</SidePanelHeader>
								<PanelScroller>
									<SlotList
										slots={slotListItems}
										selectedSlotKey={placement.selectedSlotKey}
										onSelect={placement.setSelectedSlotKey}
										onLocate={placement.setSelectedSlotKey}
										onPlace={(key) =>
											placement.onPlaceAtDefault(key, currentPageNum)
										}
									/>
								</PanelScroller>
							</>
						)}

						{/* ONE footer, whichever tab is showing: the save writes both halves, so a
						    button per tab would suggest two saves that could disagree. */}
						<PanelFooter>
							<Button
								onClick={() => void handleSave()}
								disabled={!canSave}
								loading={isSaving}
								data-testid={TEST_ELEMENT_IDS.PLACEMENT_EDITOR_SAVE_BUTTON}
							>
								Save
							</Button>
							<Button
								variant="default"
								onClick={() => {
									discard();
									panelHeaderEdit?.discard();
								}}
								disabled={(!isDirty && !panelHeaderEdit?.isDirty) || isSaving}
								data-testid="pdf-document-editor-discard"
							>
								Discard
							</Button>
						</PanelFooter>
					</SidePanel>
				)}
			</Split>
		</Flex>
	);
};
